import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Play, Pause, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { taskService } from '@/core/api';
import { useTelegram, useStore } from '@/core/providers';
import { LoadingSpinner, EmptyState } from '@/components';
import type { Task } from '@/types';

function MyTasksPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hapticFeedback, showConfirm, showAlert } = useTelegram();
  const { updateTask: updateStoreTask, removeTask } = useStore();
  const [filter, setFilter] = useState<'all' | 'active' | 'paused'>('all');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['my-tasks'],
    queryFn: () => taskService.getTasks(),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      taskService.updateTask(id, { status: status as Task['status'] }),
    onSuccess: (task) => {
      updateStoreTask(task.id, task);
      queryClient.invalidateQueries({ queryKey: ['my-tasks'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => taskService.deleteTask(id),
    onSuccess: (_, id) => {
      removeTask(id);
      queryClient.invalidateQueries({ queryKey: ['my-tasks'] });
    },
  });

  const handleToggleStatus = async (task: Task) => {
    hapticFeedback('selection');
    const newStatus = task.status === 'active' ? 'paused' : 'active';
    updateMutation.mutate({ id: task.id, status: newStatus });
  };

  const handleDelete = async (task: Task) => {
    hapticFeedback('impact');
    const confirmed = await showConfirm(
      `Are you sure you want to delete "${task.title || 'this task'}"?`
    );
    if (confirmed) {
      deleteMutation.mutate(task.id);
      showAlert('Task deleted');
    }
  };

  const tasks = data ?? [];

  const filteredTasks = tasks.filter((task) => {
    if (filter === 'all') return task.status !== 'deleted';
    return task.status === filter;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isError) {
    return (
      <EmptyState
        title="Failed to load tasks"
        description="Please check your connection and try again"
      />
    );
  }

  return (
    <div className="h-full bg-telegram-bg">
      <div
        className="pointer-events-none fixed left-1/2 z-30 -translate-x-1/2"
        style={{ top: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
      >
        <div className="pointer-events-auto flex items-center gap-2 rounded-full bg-[var(--ff-surface)]/90 px-2 py-1 shadow-[0_12px_28px_rgba(0,0,0,0.08)] backdrop-blur">
          <button
            onClick={() => navigate(-1)}
            className="rounded-full border border-[var(--ff-border)] bg-[var(--ff-surface-alt)] px-3 py-2 text-sm font-semibold text-telegram-text shadow-sm hover:shadow transition"
            aria-label="Go back"
          >
            <ArrowLeft size={18} />
          </button>
          <button
            onClick={() => navigate('/create')}
            className="rounded-full border border-[var(--ff-border)] bg-[var(--ff-surface-alt)] px-3 py-2 text-sm font-semibold text-telegram-text shadow-sm hover:shadow transition"
            aria-label="Create task"
          >
            <Plus size={18} />
          </button>
        </div>
      </div>

      {/* Filters and Tasks list */}
      <div className="p-4 pt-20 space-y-4">
        <div className="flex gap-2">
          {(['all', 'active', 'paused'] as const).map((f) => (
            <button
              key={f}
              onClick={() => {
                hapticFeedback('selection');
                setFilter(f);
              }}
              className={`rounded-full border px-3 py-2 text-sm capitalize transition-colors ${
                filter === f
                  ? 'border-[#0a84ff] bg-[var(--ff-surface)] text-[#0a84ff] shadow-sm'
                  : 'border-[var(--ff-border)] bg-[var(--ff-surface)] text-telegram-hint hover:bg-[var(--ff-surface-alt)]'
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {filteredTasks.length === 0 ? (
          <EmptyState
            title="No tasks yet"
            description="Create your first tracking task"
            action={
              <button
                onClick={() => navigate('/create')}
                className="btn-primary"
              >
                Create Task
              </button>
            }
          />
        ) : (
          filteredTasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              onToggle={() => handleToggleStatus(task)}
              onDelete={() => handleDelete(task)}
              isUpdating={updateMutation.isPending}
            />
          ))
        )}
      </div>
    </div>
  );
}

interface TaskCardProps {
  task: Task;
  onToggle: () => void;
  onDelete: () => void;
  isUpdating: boolean;
}

function TaskCard({ task, onToggle, onDelete, isUpdating }: TaskCardProps) {
  const navigate = useNavigate();

  return (
    <div
      className="card animate-slide-up cursor-pointer hover:shadow-lg transition-shadow"
      onClick={() => navigate(`/tasks/${task.id}`)}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-telegram-text line-clamp-2">
            {task.title || task.entity?.name || 'Untitled Task'}
          </h3>
          {task.description && (
            <p className="text-sm text-telegram-hint mt-1 line-clamp-2">
              {task.description}
            </p>
          )}
          <div className="flex items-center gap-2 mt-2 text-xs text-telegram-hint">
            <span className="capitalize">{task.intent_type.replace('_', ' ')}</span>
            <span>•</span>
            <span className="capitalize">{task.frequency}</span>
            <span>•</span>
            <span
              className={`px-1.5 py-0.5 rounded ${
                task.status === 'active'
                  ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400'
              }`}
            >
              {task.status}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            disabled={isUpdating}
            className="p-2 text-telegram-hint hover:text-telegram-text"
          >
            {task.status === 'active' ? (
              <Pause size={18} />
            ) : (
              <Play size={18} />
            )}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="p-2 text-telegram-hint hover:text-red-500"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default MyTasksPage;
